import {t, useSiteLanguage} from "../i18n";
import {createContext, PropsWithChildren, useContext, useEffect, useRef, useState} from 'react';
import {isReservationWidgetEnabled} from "../config/reservation";
import restaurantInfo from "../data/restaurant.json";

const ReservationBookingContext = createContext({shouldOpenWidget: false});
type Waiting = "loading" | "slow" | "error" | null;

/**
 * L'amorçage depuis le CDN, et lui seul.
 *
 * Dès que le bundle du widget est là, c'est lui qui possède l'iframe, ses délais et sa
 * fermeture. Ce contexte ne couvre que la fenêtre qui précède : entre le clic et l'arrivée du
 * script, il n'existe rien d'autre pour répondre au visiteur.
 *
 * Trois états, et leurs seuils viennent d'une mesure sur le site en production, connexion
 * bridée, trois passages :
 *
 * | | bouton cliquable | script du widget arrivé |
 * | --- | --- | --- |
 * | 1,5 Mio/s, 70 ms | 878 ms | 901 ms |
 * | 400 Kio/s, 400 ms | 1459 ms | 3898 ms |
 *
 * Sur une connexion correcte le script est là avant que le bouton n'existe : le panneau n'est
 * jamais montré. Sur une mauvaise, l'attente atteint 2,4 s — d'où le secours téléphonique
 * proposé à 3 s, et l'abandon à 10 s.
 *
 * La transmission au widget est **explicite** : `data-booking-experience` distingue un bundle
 * récent d'un ancien resté en cache, et l'échéance d'origine voyage avec l'intention pour que
 * le compte à rebours ne reparte pas de zéro.
 */
export const ReservationBookingProvider = ({children}: PropsWithChildren) => {
    useSiteLanguage();
    const [widgetEnabled] = useState(isReservationWidgetEnabled);
    const [waiting, setWaiting] = useState<Waiting>(null);
    const pending = useRef<{trigger: HTMLElement; startedAt: number} | null>(null);
    const closeButton = useRef<HTMLButtonElement>(null);
    const timers = useRef<number[]>([]);
    const lastTrigger = useRef<HTMLElement | null>(null);
    const loaderTemplate = useRef<HTMLScriptElement | null>(null);

    const stopTimers = () => {
        timers.current.forEach(window.clearTimeout);
        timers.current = [];
    };
    const close = () => {
        stopTimers();
        pending.current = null;
        setWaiting(null);
        if (lastTrigger.current?.isConnected) lastTrigger.current.focus();
    };

    useEffect(() => {
        if (waiting === "loading") closeButton.current?.focus({preventScroll: true});
    }, [waiting]);

    useEffect(() => {
        if (!widgetEnabled) return;
        loaderTemplate.current = document.querySelector<HTMLScriptElement>("#restaurant-booking-loader");
        const root = document.documentElement;
        const listening = () => root.dataset.restaurantBookingWidgetReady === "1";
        const handover = () => {
            const request = pending.current;
            if (!request || !listening()) return;
            // Le bundle récent accepte l'intention et reprend l'échéance d'origine ; un
            // ancien, resté en cache chez le visiteur, ne connaît pas cet événement et reçoit
            // son clic ordinaire. Les deux chemins existent parce que le CDN et l'iframe se
            // déploient séparément — `04 §10.3`.
            pending.current = null;
            stopTimers();
            setWaiting(null);
            if (root.getAttribute("data-booking-experience") === "1") {
                document.dispatchEvent(new CustomEvent("restaurant-booking:open", {detail: request}));
            } else {
                request.trigger.click();
            }
        };
        const observer = new MutationObserver(handover);
        observer.observe(root, {attributes: true, attributeFilter: ["data-restaurant-booking-widget-ready"]});
        const click = (event: MouseEvent) => {
            if (!(event.target instanceof Element)) return;
            const trigger = event.target.closest<HTMLElement>("[data-restaurant-booking]");
            if (!trigger || listening()) return;
            event.preventDefault();
            if (pending.current) return;
            lastTrigger.current = trigger;
            pending.current = {trigger, startedAt: Date.now()};
            stopTimers();
            setWaiting("loading");
            timers.current = [
                window.setTimeout(() => setWaiting("slow"), 3000),
                window.setTimeout(() => {pending.current = null; setWaiting("error");}, 10000),
            ];
        };
        document.addEventListener("click", click, true);
        return () => {
            observer.disconnect();
            stopTimers();
            pending.current = null;
            document.removeEventListener("click", click, true);
        };
    }, [widgetEnabled]);

    /**
     * Le déclencheur à rejouer : celui que le visiteur a touché.
     *
     * `querySelector` seul rendait le **premier** bouton de la page — souvent celui de la barre
     * de navigation, alors que le clic venait du bas de la page. Le repli sur le premier reste
     * utile si l'élément d'origine a disparu du DOM entre-temps.
     */
    const triggerToReplay = (): HTMLElement | null =>
        lastTrigger.current?.isConnected === true
            ? lastTrigger.current
            : document.querySelector<HTMLElement>("[data-restaurant-booking]");

    const retry = () => {
        close();
        if (document.documentElement.dataset.restaurantBookingWidgetReady === "1") {
            if (document.documentElement.getAttribute("data-booking-experience") === "1") {
                document.dispatchEvent(new CustomEvent("restaurant-booking:open", {
                    detail: {trigger: triggerToReplay(), startedAt: Date.now()},
                }));
            } else {
                triggerToReplay()?.click();
            }
        } else {
            // On rejoue le **même** amorçage, recopié depuis la balise d'origine : ni
            // rechargement de la page, ni nouvelle configuration devinée. Une réservation en
            // cours d'envoi ne doit jamais être relancée par une nouvelle tentative
            // d'ouverture.
            const template = loaderTemplate.current;
            if (template) {
                document.getElementById("restaurant-booking-loader")?.remove();
                delete document.documentElement.dataset.restaurantBookingLoaderLoaded;
                const script = document.createElement("script");
                for (const attribute of Array.from(template.attributes)) {
                    script.setAttribute(attribute.name, attribute.value);
                }
                script.dataset.locale = document.documentElement.lang;
                document.head.append(script);
            }
            triggerToReplay()?.click();
        }
    };

    return <ReservationBookingContext.Provider value={{shouldOpenWidget: widgetEnabled}}>
        {children}
        {waiting && <section className="reservation-wait" role="dialog" aria-label={t("booking.title")}
            onKeyDown={event => {if (event.key === "Escape") close();}}>
            <header><strong>{t("booking.title")}</strong>
                <button ref={closeButton} type="button" className="reservation-wait-close" aria-label={t("common.close")} onClick={close}>×</button>
            </header>
            {waiting !== "error" && <div className="reservation-skeleton" aria-hidden="true"><i/><i/><i/></div>}
            <p role="status" aria-live="polite">{waiting === "error"
                ? t("booking.unavailable")
                : waiting === "slow" ? t("booking.slow")
                    : t("booking.loading")}</p>
            <div className="reservation-wait-actions">
                {waiting === "error" && <button type="button" className="ui-button" onClick={retry}>{t("common.retry")}</button>}
                {waiting !== "loading" && <a href={`tel:${restaurantInfo.fixNumber}`} onClick={close}>{t("booking.call")}</a>}
            </div>
        </section>}
    </ReservationBookingContext.Provider>;
};

export const useReservationBooking = () => useContext(ReservationBookingContext);
